// Every window global the next OGS path publishes, declared once so the registry can refuse a typo.
// Notes: ~/comments/aos-threejs-poc/debugSchema_OGS_DEBUG_GLOBALS_own_set.md

import type { RenderPath, RenderSettings, RenderSettingsSource } from "./renderSettings.ts";
import type { LogLevel } from "./ogsLog.ts";
import type { RenderPlan } from "./renderPlan.ts";
import type { FaceDrivePlan } from "./faceDrivePlan.ts";
import type { RigConstraintReport } from "./rigConstraints.ts";
import type { PipelineDebug } from "./types.ts";
import type { PipelineKind } from "./pipelineKind.ts";

/** `__OGS_RENDER_PATH__` — which renderer mounted for this character, and who decided. */
export interface RenderPathDebug {
  path: RenderPath;
  source: RenderSettingsSource;
  logging: LogLevel;
  /** The refusal that forced legacy, or null. */
  error: string | null;
  /** The character the decision describes. */
  slug: string | null;
  /** 'probing' until the bundle has answered. */
  state: "probing" | "resolved";
}

/** `__OGS_RENDER_PLAN__` — what the blueprint's composite is asking for, plus its summary. */
export interface RenderPlanDebug {
  plan: RenderPlan;
  summary: string;
  /** Regions named by more than one part node — an authoring mistake. */
  duplicateRegions: string[];
}

/** `__OGS_BOOT__` — how far the bundle got, so a stalled boot names its stage. */
export interface BootDebug {
  state: "idle" | "loading" | "ready" | "error";
  slug: string | null;
  kind: PipelineKind | null;
  /** Why that kind, from resolvePipelineKind. */
  why: string | null;
  error: string | null;
}

/** `__OGS_SYNTHETIC_FACE__` — the part rig's facial joints posing the head. */
export interface SyntheticFaceDebug {
  state: "armed" | "off";
  matched: number;
  packSource: string | null;
  reason: string | null;
}

export interface OgsDebugGlobals {
  __OGS_RENDER_PATH__: RenderPathDebug;
  __OGS_RENDER_PLAN__: RenderPlanDebug;
  __OGS_BOOT__: BootDebug;
  __OGS_PIPELINE__: PipelineDebug;
  __OGS_FACE_DRIVE__: FaceDrivePlan;
  __OGS_SYNTHETIC_FACE__: SyntheticFaceDebug;
  __OGS_RIG_CONSTRAINTS__: RigConstraintReport;
  /** The avatar root group this path parents its splats to (a THREE.Group). */
  __OGS_AVATAR_ROOT_NEXT__: unknown;
}

/** The declared names. A publish outside this set throws rather than inventing a seam. */
export const OGS_DEBUG_GLOBALS = [
  "__OGS_RENDER_PATH__",
  "__OGS_RENDER_PLAN__",
  "__OGS_BOOT__",
  "__OGS_PIPELINE__",
  "__OGS_FACE_DRIVE__",
  "__OGS_SYNTHETIC_FACE__",
  "__OGS_RIG_CONSTRAINTS__",
  "__OGS_AVATAR_ROOT_NEXT__",
] as const;

export type OgsDebugName = (typeof OGS_DEBUG_GLOBALS)[number];

/** The debug view of a probe result, before a path has mounted. */
export function renderPathDebug(settings: RenderSettings, slug: string | null, state: RenderPathDebug["state"]): RenderPathDebug {
  return { path: settings.path, source: settings.source, logging: settings.logging, error: settings.error, slug, state };
}
